import { useState } from 'react';
import { blogArticles, type BlogArticle, blogCategories } from '../../../mocks/blog';
import { useSupabaseAdmin } from '../../../hooks/useSupabaseAdmin';

function articleToDb(item: Partial<BlogArticle>): Record<string, unknown> {
  return {
    slug: item.slug,
    title: item.title,
    category: item.category,
    date: item.date,
    read_time: item.readTime,
    excerpt: item.excerpt,
    image: item.image,
    content: item.content ?? [],
    tags: item.tags ?? [],
    author: item.author,
    author_role: item.authorRole,
    featured: item.featured,
    meta_description: item.metaDescription,
    keywords: item.keywords ?? [],
  };
}

function articleFromDb(row: Record<string, unknown>): BlogArticle {
  return {
    id: row.id as number,
    slug: row.slug as string,
    title: row.title as string,
    category: row.category as string,
    date: row.date as string,
    readTime: row.read_time as string,
    excerpt: row.excerpt as string,
    image: row.image as string,
    content: (row.content as string[]) ?? [],
    tags: (row.tags as string[]) ?? [],
    author: row.author as string,
    authorRole: row.author_role as string,
    featured: row.featured as boolean,
    metaDescription: row.meta_description as string,
    keywords: (row.keywords as string[]) ?? [],
  };
}

export default function AdminBlog() {
  const { data, add, update, remove, loading } = useSupabaseAdmin<BlogArticle>({
    table: 'blog_articles',
    initialData: blogArticles,
    mapToDb: articleToDb,
    mapFromDb: articleFromDb,
  });
  const [editing, setEditing] = useState<BlogArticle | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = data.filter((a) =>
    a.title.toLowerCase().includes(search.toLowerCase()) ||
    a.category.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = (formData: FormData) => {
    const article = {
      slug: formData.get('slug') as string,
      title: formData.get('title') as string,
      category: formData.get('category') as string,
      date: formData.get('date') as string,
      readTime: formData.get('readTime') as string,
      excerpt: formData.get('excerpt') as string,
      image: formData.get('image') as string,
      content: (formData.get('content') as string).split('\n\n').filter(Boolean),
      tags: (formData.get('tags') as string).split(',').map((t) => t.trim()).filter(Boolean),
      author: formData.get('author') as string,
      authorRole: formData.get('authorRole') as string,
      featured: formData.get('featured') === 'on',
      metaDescription: formData.get('metaDescription') as string,
      keywords: (formData.get('keywords') as string).split(',').map((t) => t.trim()).filter(Boolean),
    };

    if (editing) {
      update(editing.id, article);
      setEditing(null);
    } else {
      add(article);
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center gap-2 text-brand-gray-500 text-sm">
          <i className="ri-loader-4-line animate-spin" />
          Sincronizando con la nube...
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="font-serif text-brand-black text-2xl font-medium">
          Artículos <span className="text-brand-gray-400">({data.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Buscar artículo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          <button
            onClick={() => { setIsAdding(true); setEditing(null); }}
            className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
          >
            + Nuevo
          </button>
        </div>
      </div>

      {(isAdding || editing) && (
        <form
          onSubmit={(e) => { e.preventDefault(); handleSave(new FormData(e.currentTarget)); }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Artículo' : 'Nuevo Artículo'}
            </h3>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Título *</label>
              <input name="title" defaultValue={editing?.title ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Slug (URL) *</label>
              <input name="slug" defaultValue={editing?.slug ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Categoría</label>
              <select name="category" defaultValue={editing?.category ?? 'Estilo'} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black bg-white">
                {blogCategories.filter((c) => c !== 'Todos').map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Fecha</label>
              <input name="date" type="date" defaultValue={editing?.date ?? new Date().toISOString().split('T')[0]} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Extracto</label>
            <textarea name="excerpt" defaultValue={editing?.excerpt ?? ''} rows={2} maxLength={300} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" />
            <p className="font-sans text-brand-gray-400 text-xs mt-1">Máximo 300 caracteres</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Imagen (URL)</label>
              <input name="image" defaultValue={editing?.image ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div className="flex items-center gap-3 pt-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input name="featured" type="checkbox" defaultChecked={editing?.featured ?? false} className="w-4 h-4" />
                <span className="font-sans text-brand-gray-600 text-sm">Destacado en home</span>
              </label>
            </div>
          </div>

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Contenido (párrafos separados por línea en blanco)</label>
            <textarea name="content" defaultValue={editing?.content.join('\n\n') ?? ''} rows={8} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Tags (separados por coma)</label>
              <input name="tags" defaultValue={editing?.tags.join(', ') ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Keywords SEO (separadas por coma)</label>
              <input name="keywords" defaultValue={editing?.keywords.join(', ') ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Meta descripción SEO</label>
            <input name="metaDescription" defaultValue={editing?.metaDescription ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Autor</label>
              <input name="author" defaultValue={editing?.author ?? 'Anthony Sánchez'} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Rol del autor</label>
              <input name="authorRole" defaultValue={editing?.authorRole ?? 'Diseñador de Alta Costura'} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap">
              Guardar Artículo
            </button>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Artículo</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Categoría</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Fecha</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Destacado</th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {filtered.map((article) => (
                <tr key={article.id} className="hover:bg-brand-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-brand-gray-100 flex-shrink-0">
                        {article.image ? (
                          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand-gray-400 text-xs">Sin img</div>
                        )}
                      </div>
                      <div>
                        <p className="font-sans text-brand-black text-sm font-medium line-clamp-1">{article.title}</p>
                        <p className="font-sans text-brand-gray-400 text-xs">/{article.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">{article.category}</td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">{article.date}</td>
                  <td className="px-5 py-4">
                    {article.featured ? (
                      <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage text-brand-black">
                        Destacado
                      </span>
                    ) : (
                      <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-gray-200 text-brand-gray-500">
                        Normal
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setEditing(article); setIsAdding(false); }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`¿Eliminar "${article.title}"?`)) remove(article.id);
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-sans text-brand-gray-400 text-sm">No se encontraron artículos</p>
          </div>
        )}
      </div>
    </div>
  );
}